import { Card, CardContent } from "@/components/ui/card";
import { BarChart4, Package, CheckCircle, AlertTriangle } from "lucide-react";
import { formatNumber, formatCurrency } from "@/utils/numberFormatter";
import React from "react";

interface ProductSummaryProps {
  validation: { isProductionValid: boolean; isRevenueValid: boolean };
  currentProductionTarget: number;
  currentRevenueTarget: number;
  totalProduction: number;
  usedDefaultDensity: boolean;
  calculatedEntries: any[];
  productFlow: "extraction" | "transformation";
  t: any;
}

/**
 * ProductSummary
 * Display summary cards for total production and revenue.
 */
export const ProductSummary: React.FC<ProductSummaryProps> = ({
  validation,
  currentProductionTarget,
  currentRevenueTarget,
  totalProduction,
  usedDefaultDensity,
  calculatedEntries,
  productFlow,
  t,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <Card
        className={`border-2 transition-all duration-200 flex flex-col ${
          validation.isProductionValid
            ? "bg-green-50 border-green-200 hover:bg-green-100"
            : "bg-orange-50 border-orange-200 hover:bg-orange-100"
        }`}
      >
        <CardContent className="p-4 flex-1 flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-2 rounded-lg ${validation.isProductionValid ? "bg-green-100" : "bg-orange-100"}`}>
              <Package className={`h-5 w-5 ${validation.isProductionValid ? "text-green-600" : "text-orange-600"}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">
                {productFlow === "extraction"
                  ? t("products.totalProductionTargetExtraction")
                  : t("products.totalProductionTargetTransformation")}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-gray-900">{formatNumber(currentProductionTarget)} t</span>
              </div>

              {usedDefaultDensity && (
                <div className="text-xs text-yellow-600 italic mt-1">{t("products.defaultDensityApplied")}</div>
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col justify-end">
            {validation.isProductionValid ? (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t("products.currentTotal")}:</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-gray-900">{formatNumber(totalProduction)} t</span>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t("products.currentTotal")}:</span>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-gray-900">{formatNumber(totalProduction)} t</span>
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                  </div>
                </div>

                <div className="text-xs p-2 rounded bg-orange-100 text-orange-700 border border-orange-200">
                  <div className="flex items-start gap-1">
                    <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    <span>{t("products.productionValidationError")}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card
        className={`border-2 transition-all duration-200 flex flex-col ${
          validation.isRevenueValid
            ? "bg-green-50 border-green-200 hover:bg-green-100"
            : "bg-orange-50 border-orange-200 hover:bg-orange-100"
        }`}
      >
        <CardContent className="p-4 flex-1 flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-2 rounded-lg ${validation.isRevenueValid ? "bg-green-100" : "bg-orange-100"}`}>
              <BarChart4 className={`h-5 w-5 ${validation.isRevenueValid ? "text-green-600" : "text-orange-600"}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">
                {productFlow === "extraction"
                  ? t("products.totalRevenueTargetExtraction")
                  : t("products.totalRevenueTargetTransformation")}
              </p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(currentRevenueTarget)} €</p>
            </div>
          </div>

          <div className="flex-1 flex flex-col justify-end">
            {validation.isRevenueValid ? (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t("products.currentTotal")}:</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-gray-900">
                    {formatCurrency(
                      calculatedEntries.reduce((sum, entry) => sum + (Number(entry.productRevenue) || 0), 0)
                    )}{" "}
                    €
                  </span>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t("products.currentTotal")}:</span>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-gray-900">
                      {formatCurrency(
                        calculatedEntries.reduce((sum, entry) => sum + (Number(entry.productRevenue) || 0), 0)
                      )}{" "}
                      €
                    </span>
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                  </div>
                </div>

                <div className="text-xs p-2 rounded bg-orange-100 text-orange-700 border border-orange-200">
                  <div className="flex items-start gap-1">
                    <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    <span>{t("products.revenueValidationError")}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
